// Entry React. Chưa có UI thực — Phase 0 skeleton.
// Khi M1 bắt đầu port pages từ attendance-app/admin/src/pages/chat/,
// đọc docs/CODING_RULES.md + docs/ARCHITECTURE.md trước.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Sentry } from './sentry'; // init error tracking sớm (no-op nếu VITE_SENTRY_DSN trống)
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, ToastBar, toast as toastApi } from 'react-hot-toast';
import { useLingui } from '@lingui/react/macro';
import { renderableToast } from '@cnv/ui/utils/renderableToast';
// Catalog `host-admin` — chữ của HubToastBar (câu dự phòng, nút Đóng) nằm trong đó. Hôm nay LoginPage
// (App.jsx import tĩnh) cũng nạp nó, nhưng toast hiện trên MỌI trang: đừng dựa vào một trang khác tình cờ
// import trước — LoginPage mà chuyển sang lazy là bản prod lòi mã băm ngay trong toast.
import './i18n-host';
import App from './App.jsx';
import { AuthProvider } from './contexts/AuthContext';
import { VoipProvider } from './contexts/VoipContext';
import { useAuthStore } from './lib/authStore';
import { setupTokenSync } from './lib/swTokenSync';
// SW reads accessToken từ IndexedDB để fetch BE khi xử lý inline reply (chat noti).
setupTokenSync(useAuthStore);
// Drawer providers — match Core's main.jsx wrap order (tránh cascade context errors).
import { TaskDrawerProvider } from '@cnv/ui/tasks/TaskDrawerContext';
import { ApprovalDrawerProvider } from '@cnv/ui/approvals/ApprovalDrawerContext';
import { SharedListDrawerProvider } from '@cnv/ui/approvals/SharedListDrawerContext';
import { CrmContactDrawerProvider } from '@cnv/ui/crm/CrmContactDrawerContext';
import { CrmCompanyDrawerProvider } from '@cnv/ui/crm/CrmCompanyDrawerContext';
import { CrmDealDrawerProvider } from '@cnv/ui/crm/CrmDealDrawerContext';
import { CrmAccountDrawerProvider } from '@cnv/ui/crm/CrmAccountDrawerContext';
import { CrmLeadDrawerProvider } from '@cnv/ui/crm/CrmLeadDrawerContext';
import { CrmInvoiceDrawerProvider } from '@cnv/ui/crm/CrmInvoiceDrawerContext';
import { CrmQuotationDrawerProvider } from '@cnv/ui/crm/CrmQuotationDrawerContext';
// Core đã mount từ lâu; SaaS thiếu ⇒ link/card gói thuê bao trong chat rơi về nhảy trang.
import { CrmSubscriptionDrawerProvider } from '@cnv/ui/crm/CrmSubscriptionDrawerContext';
import { FulfillmentOrderDrawerProvider } from '@cnv/ui/fulfillment/FulfillmentOrderDrawerContext';
import { TicketDrawerProvider } from '@cnv/ui/tickets/TicketDrawerContext';
import ServerRetryOverlay from '@cnv/ui/common/ServerRetryOverlay';
import { I18nProvider, initI18n } from '@cnv/ui/i18n';
import '@cnv/ui/styles.css';
// Lớp phủ giao diện v2 — PHẢI nạp SAU styles.css của cnv-ui để thắng bộ remap
// `html.dark ...!important` (200 dòng, index.css:147-460). Import TĨNH ở entry để CSS
// nằm trong dist/assets/index-*.css — canary trong deploy.sh chỉ glob đúng file đó.
import '@cnv/ui/styles/ui-v2.css';
// Cụm "gói đang dùng" trên thanh trên cùng — CSS RIÊNG SaaS (Core không có cụm này nên
// không nhét vào ui-v2.css dùng chung). Import tĩnh ở entry ⇒ Vite gộp vào chính
// dist/assets/index-*.css mà canary trong deploy.sh soi, không đẻ file CSS thứ hai.
import './styles/topbar-plan.css';
// Nút «Trợ giúp» (cụm dùng chung Site Connect) — CSS ở kernel, nạp tĩnh như ui-v2-workspace.css.
import '@cnv/ui/styles/ui-v2-help.css';
// Bộ đổi workspace trên thanh trên cùng — CSS ở kernel, DÙNG CHUNG với CNV Connect SaaS (một
// bản duy nhất cho cả hai hệ SaaS). Tệp riêng chứ không nằm trong ui-v2.css vì Core cũng nạp
// ui-v2.css mà không bao giờ dựng cụm này.
import '@cnv/ui/styles/ui-v2-workspace.css';

// Chốt ngôn ngữ hiển thị TRƯỚC lần render đầu tiên (đọc localStorage đồng bộ) — nếu
// để sau, user tiếng Anh sẽ thấy tiếng Việt loé lên rồi mới đổi.
// SaaS bật cả 2; Core khoá 'vi' (User model Core chưa có cột locale) — xem docs/I18N.md.
//
// `enabled` liệt kê ngôn ngữ WEB đã có catalog thật trong cnv-ui/locales/. 'es' cố ý
// KHÔNG có mặt: es-419 mở 2026-08-09 cho mobile (CNV Connect) nên BE đã nhận mã đó,
// nhưng web chưa dịch chữ nào — locales/es/ trống 0 file. Để es lọt vào đây thì bộ chọn
// ngôn ngữ hiện "Español", user bấm vào và giao diện VẪN tiếng Việt (runtime tải hụt
// catalog rồi rơi về nguồn — an toàn, nhưng người dùng đọc là hỏng). Dịch xong web thì
// thêm 'es' vào danh sách này.
initI18n({ enabled: ['vi', 'en', 'fr'] });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Thanh toast của hub — hàng rào message. <Toaster> nằm NGOÀI Sentry.ErrorBoundary (anh em của
// <BrowserRouter>), nên một message React không vẽ được — thân lỗi SaaS `{ code, message, details }`,
// `msg`…`` quên dịch — là React #31 gỡ CẢ cây: trắng NGUYÊN hub, không có cả màn «Đã có lỗi xảy ra».
// Kernel renderableToast đổi message đó sang chữ TRƯỚC khi tới <ToastBar> (kèm console.error); ở đây bắn
// thêm về Sentry để lần ra chỗ gọi. Đọc đầu tệp kernel trước khi sửa.
// ⛔ Đừng thay bằng error boundary im lặng quanh <Toaster>: tắt cả vùng toast tới lúc F5 — hỏng câm.
// Là component riêng vì render-prop của <Toaster> chạy trong vòng lặp — gọi useLingui ở đó phạm Rules of Hooks.
// Bản anh em: cnvconnect-saas/admin/src/main.jsx (giống hệt) · attendance-app/admin/src/main.jsx (CoreToastBar).
function HubToastBar({ toast }) {
  const { t } = useLingui();
  const safe = renderableToast(toast, {
    fallback: toast.type === 'error' ? t`Có lỗi xảy ra` : t`Không hiển thị được nội dung thông báo`,
    onIssue: (issue) => Sentry.captureMessage(`toast: message không vẽ được (${issue.kind})`, {
      level: 'error',
      tags: { toast_issue: issue.kind, route: issue.route },
      extra: issue,
      fingerprint: ['toast-unrenderable', issue.kind, issue.route],
    }),
  });
  return (
    <ToastBar toast={safe}>
      {({ icon, message }) => (
        <>
          {icon}
          {message}
          {safe.type !== 'loading' && (
            <button
              onClick={() => toastApi.dismiss(safe.id)}
              className="ml-2 -mr-1 shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
              title={t`Đóng`}
              aria-label={t`Đóng thông báo`}
            >
              ×
            </button>
          )}
        </>
      )}
    </ToastBar>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    {/* I18nProvider bọc NGOÀI Router: khi đổi ngôn ngữ nó chặn render cho tới lúc catalog
        của các cụm đang mở về đủ, tránh nửa màn hình Anh nửa Việt. User tiếng Việt không
        bao giờ chạm gate này (catalog vi import tĩnh, luôn sẵn trong chunk). */}
    <I18nProvider fallback={<div className="min-h-screen" />}>
    <QueryClientProvider client={queryClient}>
      {/* v7_startTransition: điều hướng chạy trong React transition ⇒ route mới còn đang tải
          chunk thì React GIỮ trang cũ thay vì thay bằng fallback = hết trắng trang. Chỉ báo
          "đang đi" là <NavProgressBar/> trong AppLayout (@cnv/ui). */}
      <BrowserRouter future={{ v7_startTransition: true }}>
        <AuthProvider>
          <VoipProvider>
          <TaskDrawerProvider>
            <ApprovalDrawerProvider>
              <SharedListDrawerProvider>
              <TicketDrawerProvider>
                <CrmContactDrawerProvider>
                  <CrmCompanyDrawerProvider>
                    <CrmDealDrawerProvider>
                      <CrmAccountDrawerProvider>
                        <CrmLeadDrawerProvider>
                          <CrmInvoiceDrawerProvider>
                            <CrmQuotationDrawerProvider>
                              <CrmSubscriptionDrawerProvider>
                              <FulfillmentOrderDrawerProvider>
                              <Sentry.ErrorBoundary
                                fallback={() => (
                                  <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
                                    <p>Đã có lỗi xảy ra. Vui lòng tải lại trang.</p>
                                    <button onClick={() => window.location.reload()}
                                      style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, background: '#2563eb', color: '#fff', border: 0, cursor: 'pointer' }}>
                                      Tải lại
                                    </button>
                                  </div>
                                )}>
                                <App />
                              </Sentry.ErrorBoundary>
                              </FulfillmentOrderDrawerProvider>
                              </CrmSubscriptionDrawerProvider>
                            </CrmQuotationDrawerProvider>
                          </CrmInvoiceDrawerProvider>
                        </CrmLeadDrawerProvider>
                      </CrmAccountDrawerProvider>
                    </CrmDealDrawerProvider>
                  </CrmCompanyDrawerProvider>
                </CrmContactDrawerProvider>
              </TicketDrawerProvider>
              </SharedListDrawerProvider>
            </ApprovalDrawerProvider>
          </TaskDrawerProvider>
          </VoipProvider>
        </AuthProvider>
      </BrowserRouter>
      {/* Toast với nút × để user chủ động đóng — match Core admin pattern */}
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 100000 }}
        toastOptions={{
          duration: 3500,
          style: { fontSize: '14px', zIndex: 100000 },
          error: { duration: 5000, style: { fontSize: '14px', zIndex: 100000, maxWidth: '480px' } },
        }}
      >
        {(t) => <HubToastBar toast={t} />}
      </Toaster>
      {/* Overlay "Kết nối Internet không ổn định" — nghe event server-retry từ inboxClient */}
      <ServerRetryOverlay />
    </QueryClientProvider>
    </I18nProvider>
  </React.StrictMode>,
);
